import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "~/components/ui/dialog";
import { Button } from "~/components/ui/button";
import { Badge } from "~/components/ui/badge";
import { Switch } from "~/components/ui/switch";
import { PolicyForm } from "./policy-form";
import type { PolicyFormData } from "./policy-form";
import { Plus, Trash2, Pencil, Loader2, Shield, AlertTriangle } from "lucide-react";
import { PolicySimulation } from "./policy-simulation";
import { PolicyDiffDialog } from "./policy-diff-dialog";
import { RiskPanel } from "./risk-panel";
import { toast } from "sonner";

interface Policy {
  name: string;
  command: string;
  permissive: string;
  using_expression: string | null;
  with_check_expression: string | null;
  roles: string[] | string;
}

interface PolicyGroup {
  baseName: string;
  commands: string[];
  policies: Policy[];
  permissive: string;
  using_expression: string | null;
  with_check_expression: string | null;
  roles: string[] | string;
}

/** Normalize roles to a comparable string, handling Postgres array format. */
function normalizeRoles(r: string[] | string): string {
  if (Array.isArray(r)) return r.sort().join(",");
  if (typeof r === "string" && r.startsWith("{") && r.endsWith("}")) {
    return r.slice(1, -1).split(",").sort().join(",");
  }
  return r || "";
}

/** Extract base name by stripping command suffix and optional group ID. */
function deriveBaseName(name: string): string {
  // Pattern: name_command_groupid (e.g. "my_policy_insert_abc123")
  return name.replace(/_(select|insert|update|delete|all)(_[a-z0-9]+)?$/i, "");
}

/** Group policies that share the same expressions, permissive flag, and roles. */
function groupPolicies(policies: Policy[]): PolicyGroup[] {
  const groups: PolicyGroup[] = [];

  for (const p of policies) {
    const rolesStr = normalizeRoles(p.roles);
    const existing = groups.find(
      (g) =>
        g.using_expression === p.using_expression &&
        g.with_check_expression === p.with_check_expression &&
        g.permissive === p.permissive &&
        normalizeRoles(g.roles) === rolesStr &&
        g.commands[0] !== "ALL" && p.command !== "ALL",
    );

    if (existing) {
      existing.commands.push(p.command);
      existing.policies.push(p);
    } else {
      groups.push({
        baseName: deriveBaseName(p.name),
        commands: [p.command],
        policies: [p],
        permissive: p.permissive,
        using_expression: p.using_expression,
        with_check_expression: p.with_check_expression,
        roles: p.roles,
      });
    }
  }

  return groups;
}

export function RlsPoliciesPanel({
  open,
  onOpenChange,
  schema,
  table,
  rlsEnabled,
  isProtected,
  onRlsToggle,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schema: string;
  table: string;
  rlsEnabled: boolean;
  isProtected: boolean;
  onRlsToggle: () => void;
}) {
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingGroup, setEditingGroup] = useState<PolicyGroup | null>(null);
  const [toggling, setToggling] = useState(false);
  const [diffState, setDiffState] = useState<{
    current: Policy | null;
    proposed: PolicyFormData;
    group?: PolicyGroup;
  } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PolicyGroup | null>(null);
  const [rowCount, setRowCount] = useState<number | null>(null);

  const groups = useMemo(() => groupPolicies(policies), [policies]);

  function fetchPolicies() {
    setLoading(true);
    fetch(`/api/database/policies?schema=${encodeURIComponent(schema)}&table=${encodeURIComponent(table)}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.error) setError(data.error);
        else setPolicies(data.policies || []);
        setLoading(false);
      })
      .catch((err) => { setError(err.message); setLoading(false); });
  }

  useEffect(() => {
    if (open) fetchPolicies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, schema, table]);

  async function handleToggleRls() {
    setToggling(true);
    try {
      const res = await fetch("/api/database/policies", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "toggle_rls", schema, table, enable: !rlsEnabled }),
      });
      if (res.ok) { onRlsToggle(); toast.success(`RLS ${!rlsEnabled ? "enabled" : "disabled"}`); }
    } catch { toast.error("Failed to toggle RLS"); }
    finally { setToggling(false); }
  }

/**
 * The expressions Postgres will accept for a given command.
 *
 * INSERT takes WITH CHECK only, SELECT and DELETE take USING only, and UPDATE
 * and ALL take both. An INSERT policy built from a preset arrives with both
 * filled in (they are the same expression), so the USING side is dropped
 * rather than treated as an error.
 */
function expressionsFor(command: string, using?: string, withCheck?: string) {
  const cmd = command.toUpperCase();
  if (cmd === "INSERT") return { withCheck: withCheck || using || undefined };
  if (cmd === "SELECT" || cmd === "DELETE") return { using: using || undefined };
  return { using: using || undefined, withCheck: withCheck || undefined };
}

  async function createSinglePolicy(data: PolicyFormData) {
    const res = await fetch("/api/database/policies", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action: "create", schema, table,
        name: data.name, command: data.command, permissive: data.permissive,
        // Postgres accepts only WITH CHECK on INSERT and only USING on SELECT
        // and DELETE; UPDATE and ALL take both. Sending the pair regardless
        // meant every INSERT-only policy was rejected with
        // "only WITH CHECK expression allowed for INSERT".
        ...expressionsFor(data.command, data.using, data.withCheck),
        roles: data.roles ? data.roles.split(",").map((r) => r.trim()).filter(Boolean) : undefined,
      }),
    });
    const result = await res.json();
    if (!res.ok) throw new Error(result.error || "Failed to create policy");
  }

  async function handleCreatePolicy(data: PolicyFormData) {
    setDiffState({ current: null, proposed: data });
  }

  async function executeCreatePolicy(data: PolicyFormData) {
    await createSinglePolicy(data);
    setShowCreate(false);
    fetchPolicies();
    toast.success("Policy created");
  }

  async function handleUpdateGroup(group: PolicyGroup, data: PolicyFormData) {
    // Show diff dialog before applying changes
    const currentPolicy: Policy = {
      name: group.baseName,
      command: group.commands.join(", "),
      permissive: group.permissive,
      using_expression: group.using_expression,
      with_check_expression: group.with_check_expression,
      roles: group.roles,
    };
    setDiffState({ current: currentPolicy, proposed: data, group });
  }

  async function executeUpdateGroup(group: PolicyGroup, data: PolicyFormData) {
    // Delete all old policies in the group
    for (const p of group.policies) {
      await fetch(
        `/api/database/policies?schema=${encodeURIComponent(schema)}&table=${encodeURIComponent(table)}&name=${encodeURIComponent(p.name)}`,
        { method: "DELETE" },
      );
    }
    // Create new policy (the form handles multi-command by calling onSubmit multiple times)
    await createSinglePolicy(data);
    setEditingGroup(null);
    fetchPolicies();
    toast.success("Policy updated");
  }

  async function handleDeleteGroup(group: PolicyGroup) {
    // Fetch row count for the risk panel
    setDeleteTarget(group);
    try {
      const res = await fetch("/api/database/query", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sql: `SELECT COUNT(*)::int AS count FROM "${table}"` }),
      });
      const data = await res.json();
      setRowCount(data.rows?.[0]?.count ?? null);
    } catch {
      setRowCount(null);
    }
  }

  async function confirmDeleteGroup() {
    if (!deleteTarget) return;
    for (const p of deleteTarget.policies) {
      await fetch(
        `/api/database/policies?schema=${encodeURIComponent(schema)}&table=${encodeURIComponent(table)}&name=${encodeURIComponent(p.name)}`,
        { method: "DELETE" },
      );
    }
    setDeleteTarget(null);
    setRowCount(null);
    fetchPolicies();
    toast.success(deleteTarget.policies.length > 1 ? `${deleteTarget.policies.length} policies deleted` : "Policy deleted");
  }

  function rolesStr(r: string[] | string): string {
    if (Array.isArray(r)) return r.join(", ");
    if (typeof r === "string" && r.startsWith("{") && r.endsWith("}")) {
      return r.slice(1, -1).split(",").join(", ");
    }
    return r || "public";
  }

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Row Level Security
          </DialogTitle>
          <DialogDescription>
            Manage RLS policies for <span className="font-mono text-foreground">{schema}.{table}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4">
          {/* RLS toggle */}
          <div className="flex items-center justify-between rounded-md border border-border px-4 py-3">
            <div>
              <p className="text-sm font-medium">Row Level Security is {rlsEnabled ? "enabled" : "disabled"}</p>
              <p className="text-sm text-muted-foreground">
                {rlsEnabled ? "Queries are filtered by active policies" : "All rows are accessible without restriction"}
              </p>
            </div>
            <Switch checked={rlsEnabled} onCheckedChange={handleToggleRls} disabled={isProtected || toggling} />
          </div>

          {/* Policies list */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <p className="text-xs text-destructive">{error}</p>
          ) : (
            <>
              {groups.length === 0 ? (
                <div className="text-center py-8">
                  <Shield className="mx-auto h-6 w-6 text-muted-foreground/30" />
                  <p className="mt-2 text-sm text-muted-foreground">No policies defined</p>
                  {!isProtected && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {rlsEnabled ? "RLS is enabled but no policies exist, so all access is denied" : "Enable RLS and create policies to restrict access"}
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {groups.map((group) =>
                    editingGroup?.baseName === group.baseName ? (
                      <PolicyForm
                        key={group.baseName}
                        initial={{
                          name: group.baseName,
                          command: group.commands.length === 1 ? group.commands[0] : group.commands[0],
                          commands: group.commands,
                          permissive: group.permissive === "PERMISSIVE",
                          using: group.using_expression || "",
                          withCheck: group.with_check_expression || "",
                          roles: rolesStr(group.roles),
                        }}
                        onSubmit={(data) => handleUpdateGroup(group, data)}
                        onCancel={() => setEditingGroup(null)}
                        submitLabel="Update Policy"
                      />
                    ) : (
                      <div key={group.baseName} className="border border-border rounded-md px-4 py-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-sm font-medium">{group.baseName}</span>
                            {group.commands.map((cmd) => (
                              <Badge key={cmd} variant="secondary" className="text-xs">{cmd}</Badge>
                            ))}
                            <Badge className={group.permissive === "PERMISSIVE" ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20 text-xs" : "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20 text-xs"}>
                              {group.permissive}
                            </Badge>
                          </div>
                          {!isProtected && (
                            <div className="flex items-center gap-1">
                              <Button aria-label={`Edit policy ${group.baseName}`} variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setEditingGroup(group)}>
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <Button aria-label={`Delete policy ${group.baseName}`} variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive" onClick={() => handleDeleteGroup(group)}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                        </div>
                        {group.using_expression && (
                          <div>
                            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-0.5">Using</p>
                            <pre className="text-xs font-mono bg-secondary/50 rounded px-2 py-1.5 overflow-x-auto">{group.using_expression}</pre>
                          </div>
                        )}
                        {group.with_check_expression && (
                          <div>
                            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-0.5">With Check</p>
                            <pre className="text-xs font-mono bg-secondary/50 rounded px-2 py-1.5 overflow-x-auto">{group.with_check_expression}</pre>
                          </div>
                        )}
                        {group.roles && (
                          <p className="text-xs text-muted-foreground">Roles: {rolesStr(group.roles)}</p>
                        )}
                      </div>
                    ),
                  )}
                </div>
              )}

              {/* Create policy */}
              {!isProtected && (
                showCreate ? (
                  <PolicyForm
                    onSubmit={handleCreatePolicy}
                    onCancel={() => setShowCreate(false)}
                    submitLabel="Create Policy"
                  />
                ) : (
                  <Button variant="outline" size="sm" className="w-full text-sm gap-1.5" onClick={() => setShowCreate(true)}>
                    <Plus className="h-3 w-3" />
                    Create Policy
                  </Button>
                )
              )}
            </>
          )}

          {/* Coverage gap warnings */}
          {rlsEnabled && groups.length > 0 && (() => {
            const coveredCommands = new Set<string>();
            for (const g of groups) {
              for (const cmd of g.commands) {
                if (cmd === "ALL") { coveredCommands.add("SELECT"); coveredCommands.add("INSERT"); coveredCommands.add("UPDATE"); coveredCommands.add("DELETE"); }
                else coveredCommands.add(cmd);
              }
            }
            const allCommands = ["SELECT", "INSERT", "UPDATE", "DELETE"];
            const missing = allCommands.filter((c) => !coveredCommands.has(c));
            if (missing.length === 0) return null;
            return (
              <RiskPanel
                message={`Policy gap: no ${missing.join(", ")} ${missing.length === 1 ? "policy" : "policies"} defined.`}
                detail={`RLS is enabled but ${missing.join(", ")} operations have no policy. These operations will be denied for all users.`}
                severity="warning"
              />
            );
          })()}

          {/* Simulation Preview */}
          <PolicySimulation schema={schema} table={table} rlsEnabled={rlsEnabled} />
        </div>
      </DialogContent>
    </Dialog>

    {/* Policy diff confirmation dialog */}
    {diffState && (
      <PolicyDiffDialog
        open={!!diffState}
        onOpenChange={(v) => { if (!v) setDiffState(null); }}
        currentPolicy={diffState.current}
        proposedPolicy={diffState.proposed}
        onConfirm={async () => {
          if (diffState.current && diffState.group) {
            await executeUpdateGroup(diffState.group, diffState.proposed);
          } else {
            await executeCreatePolicy(diffState.proposed);
          }
          setDiffState(null);
        }}
      />
    )}

    {/* Policy delete confirmation dialog with risk panel */}
    {deleteTarget && (
      <Dialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) { setDeleteTarget(null); setRowCount(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Delete Policy
            </DialogTitle>
            <DialogDescription>
              Remove the policy <span className="font-mono text-foreground">{deleteTarget.baseName}</span> from{" "}
              <span className="font-mono text-foreground">{schema}.{table}</span>?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <RiskPanel
              message={`This policy affects ${rowCount !== null ? `${rowCount.toLocaleString()} rows in` : ""} table ${table}.`}
              detail="Removing it may expose data or deny all access, depending on other active policies."
              severity="warning"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => { setDeleteTarget(null); setRowCount(null); }}>
              Cancel
            </Button>
            <Button variant="destructive" size="sm" onClick={confirmDeleteGroup}>
              Delete Policy
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    )}
    </>
  );
}
